import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Home, QrCode, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { containersApi, formatError } from "../lib/api";
import { formatDate } from "../lib/format";
import { toast } from "sonner";

const PARTICLES = [
  { x: -70, y: -45, delay: 0.05, color: "#4A8A78" },
  { x:  70, y: -55, delay: 0.10, color: "#1F5566" },
  { x: -85, y:  20, delay: 0.15, color: "#E07A5F" },
  { x:  85, y:  25, delay: 0.20, color: "#277A82" },
  { x: -40, y:  60, delay: 0.25, color: "#4A8A78" },
  { x:  45, y:  60, delay: 0.30, color: "#1F5566" },
  { x:   0, y: -75, delay: 0.35, color: "#E07A5F" },
];

const ContainerSaved = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [container, setContainer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    containersApi
      .get(id)
      .then((c) => { if (alive) setContainer(c); })
      .catch((err) => { if (alive) toast.error(formatError(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const openScanner = () => {
    navigate("/?scan=1");
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-[#4F5D75]">
        Loading…
      </div>
    );
  }

  return (
    <div className="px-5 pt-10 pb-8 flex flex-col items-center text-center max-w-md mx-auto">
      {/* Check badge */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 16, delay: 0.05 }}
        data-testid="saved-check-badge"
        className="w-24 h-24 rounded-full bg-[#4A8A78] flex items-center justify-center shadow-[0_12px_30px_rgba(74,138,120,0.45)]"
      >
        <Check className="w-12 h-12 text-white" strokeWidth={3.5} />
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="font-heading text-3xl font-bold text-[#2D3142] mt-6"
        data-testid="saved-heading"
      >
        Item Saved!
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.32 }}
        data-testid="saved-item-name"
        className="font-heading text-xl font-semibold text-[#1F5566] mt-3"
      >
        {container?.name || "Your container"}
      </motion.div>

      {container?.expiration_date && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.38 }}
          className="mt-3"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">
            Will be best eaten by
          </div>
          <div
            className="font-heading text-lg font-bold text-[#2D3142] mt-1"
            data-testid="saved-expiry-date"
          >
            {formatDate(container.expiration_date)}
          </div>
        </motion.div>
      )}

      {/* Container + particles mockup */}
      <div className="relative mt-8 mb-6 h-[170px] w-full flex items-center justify-center pointer-events-none">
        {PARTICLES.map((p) => (
          <motion.span
            key={`${p.x}-${p.y}-${p.color}`}
            initial={{ opacity: 0, x: 0, y: 0, scale: 0.4 }}
            animate={{ opacity: [0, 1, 1, 0], x: p.x, y: p.y, scale: [0.4, 1.1, 1, 0.9] }}
            transition={{ duration: 1.6, delay: p.delay, repeat: Infinity, repeatDelay: 1.6 }}
            className="absolute w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: p.color }}
          />
        ))}
        <motion.div
          initial={{ scale: 0.7, opacity: 0, rotate: -6 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
          className="relative w-[110px] h-[110px] rounded-3xl bg-gradient-to-br from-[#DCEDF0] to-[#F2F7F3] border-2 border-[#277A82]/40 flex items-center justify-center shadow-md"
          data-testid="saved-container-mockup"
        >
          <Package className="w-14 h-14 text-[#1F5566]" strokeWidth={1.6} />
          <div className="absolute -bottom-2 -right-2 w-9 h-9 rounded-xl brand-gradient-bg flex items-center justify-center shadow-lg">
            <QrCode className="w-4 h-4 text-white" />
          </div>
        </motion.div>
      </div>

      <Button
        onClick={() => navigate("/")}
        data-testid="saved-return-home-btn"
        className="w-full h-14 brand-gradient-bg text-white text-base font-semibold hover:opacity-90"
      >
        <Home className="w-5 h-5 mr-2" /> Return to Home
      </Button>

      <Button
        onClick={openScanner}
        data-testid="saved-log-another-btn"
        variant="outline"
        className="w-full h-14 mt-3 border-2 border-[#277A82] text-[#1F5566] text-base font-semibold hover:bg-[#DCEDF0]"
      >
        <QrCode className="w-5 h-5 mr-2" /> Log Another Container
      </Button>
    </div>
  );
};

export default ContainerSaved;
